import React from 'react'
import PropTypes from 'prop-types'
import Logo from 'js/components/Logo'
import Button from 'js/components/Button/Button'
import useBreakpoints from 'js/hooks/useBreakpoints'
import { Link } from 'react-router-dom'
import AppButtons from 'js/components/AppButtons/AppButtons'
import Mask from 'js/components/Mask'
import Header from 'js/components/Header/Header'
import BalanceText from 'js/components/BalanceText'
import cn from 'classnames'
import convert from 'htmr'

function HeaderButtons({ buttonText, buttonLink, nativeAd }) {
  const { LG } = useBreakpoints()
  return nativeAd ? (
    <div className="text-center lg:text-left">
      <AppButtons />
    </div>
  ) : (
    <div
      className={cn('mx-auto flex md:max-w-512 lg:block lg:max-w-none', {
        'text-center': false,
      })}
    >
      <Button
        color="white"
        big
        className="mb-16 md:mx-8 lg:mb-0 lg:ml-0 lg:mr-24"
        block={!LG}
        use="a"
        href="#start"
      >
        Learn more
      </Button>
      {buttonText && (
        <Button
          color="white"
          className="md:mx-8 lg:mx-0"
          outline
          big
          block={!LG}
          use={Link}
          to={buttonLink}
        >
          {buttonText}
        </Button>
      )}
    </div>
  )
}

HeaderButtons.propTypes = {
  buttonLink: PropTypes.string,
  buttonText: PropTypes.string,
  nativeAd: PropTypes.bool,
}

function DatoPageHeader({
  defaultLayout = true,
  screenshot,
  heading,
  preamble,
  // buttonText,
  // buttonLink = '/signup',
  showButtons = true,
  firstSectionBackground = 'white',
  backgroundImage,
  overlayGradient,
  showHeader,
  partnerLogo,
  ...props
}) {
  return (
    <div className={firstSectionBackground}>
      {showHeader && <Header fixedPosition />}
      <Mask>
        <header className="relative overflow-hidden bg-green">
          <div className="relative z-10 mx-auto flex w-full max-w-1280 items-center justify-center px-32 pt-24 lg:pt-32">
            {!showHeader && <Logo inverted />}
            {partnerLogo && (
              <>
                <span className="xl:px-50 px-20 text-white type-24-demibold md:px-30 lg:px-40">
                  +
                </span>
                <span className="max-w-1/2">
                  <img src={partnerLogo.url} alt="" className="h-32 md:h-56" />
                </span>
              </>
            )}
          </div>
          <section
            className="relative z-10 mx-auto flex min-h-512 w-full max-w-1280 flex-col justify-center px-32 lg:flex-row"
            style={{
              top: '-45px',
            }}
          >
            <div className="flex flex-col items-center justify-center py-0 lg:py-72 xl:py-128">
              <BalanceText>
                <h1 className="mb-24 text-center text-40 font-bold text-white lg:mb-0 lg:py-36 lg:text-left lg:text-64">
                  {heading}
                </h1>
              </BalanceText>
              <div className="mx-auto max-w-576 text-center font-bold leading-body text-white md:type-20 lg:max-w-768 lg:type-24 xxl:type-28">
                {convert(preamble, {
                  transform: {
                    // eslint-disable-next-line react/prop-types
                    a: function Anchor({ children, href }) {
                      return (
                        <a href={href} className="text-white underline">
                          {children}
                        </a>
                      )
                    },
                  },
                })}
              </div>
              {showButtons && <HeaderButtons />}
            </div>
            {defaultLayout && screenshot && (
              <div className="mb-16 flex shrink-0 justify-center lg:mx-40 lg:mb-36">
                <picture>
                  <source
                    media="(max-width: 800px)"
                    srcSet={`${screenshot}&w=200`}
                  />
                  <source
                    media="(min-width: 800px)"
                    srcSet={`${screenshot}&w=400&fit=crop&crop=faces,edges`}
                    sizes={'100vw'}
                  />
                  <img
                    src={screenshot}
                    alt=""
                    className="w-full"
                    style={{
                      objectFit: 'cover',
                      objectPosition: '0 35%',
                      height: '100%',
                    }}
                  />
                </picture>
              </div>
            )}
          </section>
          {props.children}
          {backgroundImage && (
            <picture className="absolute bottom-0 left-0 right-0 top-0">
              <source
                media="(max-width: 800px)"
                srcSet={backgroundImage.mobileImage}
              />
              <source
                media="(min-width: 800px)"
                srcSet={backgroundImage.responsiveImage.srcSet}
                sizes={'100vw'}
              />
              <img
                src={backgroundImage.responsiveImage.src}
                alt=""
                className="w-full"
                style={{
                  objectFit: 'cover',
                  objectPosition: '0 35%',
                  height: '100%',
                }}
              />
            </picture>
          )}
          {overlayGradient && (
            <div
              style={{
                zIndex: 2,
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  'linear-gradient(162deg,transparent,rgba(0,0,0,.8))',
              }}
            ></div>
          )}
        </header>
      </Mask>
      {props.headerCta && (
        <Button
          className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 transform"
          big
          color="premium"
          use="a"
          href={props.headerCta.url}
          nudge={false}
        >
          {props.headerCta.buttonText}
        </Button>
      )}
    </div>
  )
}

DatoPageHeader.propTypes = {
  nativeAd: PropTypes.bool.isRequired,
  heading: PropTypes.any.isRequired,
  preamble: PropTypes.string,
  buttonText: PropTypes.any,
  buttonLink: PropTypes.string,
  children: PropTypes.any,
  defaultLayout: PropTypes.bool,
  screenshot: PropTypes.string,
  showButtons: PropTypes.bool,
  firstSectionBackground: PropTypes.string,
  backgroundImage: PropTypes.object,
  overlayGradient: PropTypes.bool,
  showHeader: PropTypes.bool,
  headerCta: PropTypes.shape({
    buttonText: PropTypes.string,
    url: PropTypes.string,
  }),
  partnerLogo: PropTypes.shape({
    url: PropTypes.string,
  }),
}

export default DatoPageHeader
